import type { Command } from '../../menu/types';
import { getCommandId, hasCommand } from '../../grid/commands/utils';
import {
  useGetEmployeeReviewCommandAvailabilityQuery,
  type EmployeeReviewCommandAvailability,
} from '../api/employeeReviewApi';

const submitCommandId = 'employeereviewsubmit';
const approveCommandId = 'employeereviewapprovebymanager';

export const isEmployeeReviewActionCommand = (command: Command): boolean => {
  const commandId = getCommandId(command);
  return commandId === submitCommandId || commandId === approveCommandId;
};

export const isEmployeeReviewCommandDisabled = (
  command: Command,
  availability: Pick<EmployeeReviewCommandAvailability, 'canSubmit' | 'canApprove'> | undefined,
  isLoadingOrError: boolean
): boolean => {
  const commandId = getCommandId(command);
  if (commandId === submitCommandId) {
    return isLoadingOrError || !availability?.canSubmit;
  }

  if (commandId === approveCommandId) {
    return isLoadingOrError || !availability?.canApprove;
  }

  return false;
};

export const useEmployeeReviewCommandAvailability = (
  commands: Command[] | undefined,
  entityId: unknown
) => {
  const hasManagerReviewAction = hasCommand(commands, approveCommandId);
  const hasReviewAction = hasCommand(commands, submitCommandId) || hasManagerReviewAction;
  const reviewId = Number(entityId);
  const hasPersistedReview = Number.isInteger(reviewId) && reviewId > 0;
  const query = useGetEmployeeReviewCommandAvailabilityQuery(reviewId, {
    skip: !hasReviewAction || !hasPersistedReview,
    refetchOnMountOrArgChange: true,
  });

  return {
    hasManagerReviewAction,
    isDisabled: (command: Command) =>
      isEmployeeReviewCommandDisabled(
        command,
        query.data,
        query.isLoading || query.isFetching || query.isError || !hasPersistedReview
      ),
  };
};
